import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Outlet, redirect, type LoaderFunctionArgs, type ShouldRevalidateFunctionArgs } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { initializeHopscotch } from './lib/hopscotch';

// Styles
import { Theme } from '@radix-ui/themes';
import './index.css';

// Components
import { SandboxEditor } from './components/sandbox-editor/sandbox-editor';
import { ZyXmlConverter } from './components/sandbox-editor/zyxml-converter';
import { BookInfo } from './components/search/book-details.tsx';
import { TagInfo } from './components/search/tag-details.tsx';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import AuthenticatedShell from './pages/AuthenticatedShell.tsx';
import BatchDiff from './pages/BatchDiff.tsx';
import Books, { loader as booksLoader, shouldRevalidate as shouldBooksRevalidate } from './pages/Books.tsx';
import CompareBooks from './pages/CompareBooks.tsx';
import Document from './pages/Document.tsx';
import DocumentCompare from './pages/DocumentCompare.tsx';
import EditorDev from './pages/EditorDev.tsx';
import ImageLibraryPage from './pages/Images.tsx';
import Login from './pages/Login.tsx';
import NotFound from './pages/NotFound.tsx';
import RouteErrorFallback from './pages/RouteErrorFallback.tsx';
import Sandbox from './pages/Sandbox.tsx';
import Search, { searchLoader } from './pages/Search.tsx';
import ZybookImport from './pages/ZybookImport.tsx';
import { ZybookAuthProvider } from './providers/zybook-auth-provider.tsx';
import authService from './services/zybook-auth-service.ts';

function shouldRequireAuthRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }: ShouldRevalidateFunctionArgs): boolean {
    if (currentUrl.pathname !== nextUrl.pathname) {
        return true;
    }
    if (currentUrl.search !== nextUrl.search) {
        return false;
    }
    return defaultShouldRevalidate;
}

function requireAuth({ request }: LoaderFunctionArgs) {
    if (!authService.isAuthenticated()) {
        const url = new URL(request.url);
        const redirectTo = url.pathname + url.search;
        return redirect(`/login?redirectTo=${encodeURIComponent(redirectTo)}`);
    }

    // If user cannot see all MA content, redirect to /demo
    if (!authService.canSeeAllMAContent()) {
        const url = new URL(request.url);
        const allowedPaths = ['/demo'];
        const disallowedPaths = ['/demo/converter', '/demo/batch-diff', '/demo/document-compare'];
        const isAllowedPath = allowedPaths.some(path => url.pathname.startsWith(path));
        const isDisallowedPath = disallowedPaths.some(path => url.pathname.startsWith(path));
        if (isDisallowedPath || !isAllowedPath) {
            return redirect('/demo');
        }
    }

    return null;
}

function requireAdminAccess() {
    if (!authService.canAccessAdmin()) {
        return redirect('/');
    }
    return null;
}

async function loginLoader({ request }: LoaderFunctionArgs) {
    if (authService.isAuthenticated()) {
        const url = new URL(request.url);
        const redirectTo = url.searchParams.get('redirectTo') || '/books';
        return redirect(redirectTo);
    }
    return null;
}

const router = createBrowserRouter([
    {
        element: <Outlet />,
        errorElement: <RouteErrorFallback />,
        children: [
            {
                path: '/login',
                element: <Login />,
                loader: loginLoader,
            },
            {
                path: '/',
                element: <AuthenticatedShell />,
                loader: requireAuth,
                shouldRevalidate: shouldRequireAuthRevalidate,
                children: [
                    {
                        index: true,
                        loader: () => redirect('/books'),
                    },
                    {
                        path: 'books',
                        element: <Outlet />,
                        children: [
                            {
                                index: true,
                                Component: Books,
                                loader: booksLoader,
                                shouldRevalidate: shouldBooksRevalidate,
                            },
                            {
                                path: ':bookName',
                                id: 'book-toc',
                                lazy: async () => {
                                    const [page, loader, handle] = await Promise.all([
                                        import('./pages/BookToc.tsx'),
                                        import('./pages/book-toc.loader.ts'),
                                        import('./pages/book-toc.handle.ts'),
                                    ]);
                                    return {
                                        Component: page.default,
                                        loader: loader.clientLoader,
                                        shouldRevalidate: loader.shouldRevalidate,
                                        handle: handle.activitySidebarHandle,
                                        HydrateFallback: page.HydrateFallback,
                                        ErrorBoundary: page.ErrorBoundary,
                                    };
                                },
                            },
                            {
                                path: ':bookName/review',
                                id: 'book-review',
                                lazy: async () => {
                                    const m = await import('./pages/BookReview.tsx');
                                    return {
                                        Component: m.default,
                                        loader: m.clientLoader,
                                    };
                                },
                            },
                            {
                                path: ':bookName/:sectionName',
                                id: 'book-section',
                                lazy: async () => {
                                    const m = await import('./pages/Section.tsx');
                                    return {
                                        Component: m.default,
                                        loader: m.clientLoader,
                                        shouldRevalidate: m.shouldRevalidate,
                                    };
                                },
                            },
                        ],
                    },
                    {
                        path: 'subjects',
                        element: <Outlet />,
                        children: [
                            {
                                index: true,
                                lazy: async () => {
                                    const m = await import('./pages/Subjects.tsx');
                                    return {
                                        Component: m.default,
                                        loader: m.loader,
                                        shouldRevalidate: m.shouldRevalidate,
                                    };
                                },
                            },
                            {
                                path: ':subjectId',
                                lazy: async () => {
                                    const m = await import('./pages/Subject.tsx');
                                    return {
                                        Component: m.default,
                                        loader: m.loader,
                                        shouldRevalidate: m.shouldRevalidate,
                                    };
                                },
                            },
                        ],
                    },
                    {
                        path: 'editor-dev',
                        element: <EditorDev />,
                    },
                    {
                        path: 'search',
                        element: <Search />,
                        loader: searchLoader,
                        children: [
                            {
                                index: true,
                                element: <TagInfo />,
                            },
                            {
                                path: 'tag/:tagId',
                                element: <TagInfo />,
                            },
                            {
                                path: 'book/:bookId',
                                element: <BookInfo />,
                            },
                        ],
                    },
                    {
                        path: 'images',
                        element: <ImageLibraryPage />,
                    },
                    {
                        path: 'documents/:contentSource/:releaseNumber/images',
                        loader: () => redirect('/images'),
                    },
                    {
                        path: 'documents/:documentId',
                        element: <Document />,
                    },
                    {
                        path: 'compare-books',
                        element: <CompareBooks />,
                    },
                    {
                        path: 'document-compare',
                        element: <DocumentCompare />,
                    },
                    {
                        path: 'demo',
                        element: <Sandbox />,
                        children: [
                            {
                                index: true,
                                element: <SandboxEditor />,
                            },
                            {
                                path: 'converter',
                                element: <ZyXmlConverter />,
                            },
                            {
                                path: 'batch-diff',
                                element: <BatchDiff />,
                            },
                            {
                                path: ':demoId',
                                element: <SandboxEditor />,
                            },
                        ],
                    },
                    {
                        path: '/import',
                        element: <ZybookImport />,
                        loader: requireAuth,
                    },
                    {
                        path: 'admin',
                        element: <Outlet />,
                        loader: requireAdminAccess,
                        children: [
                            {
                                index: true,
                                lazy: async () => {
                                    const m = await import('./pages/Admin.tsx');
                                    return { Component: m.default };
                                },
                            },
                            {
                                path: 'delete-orphans',
                                lazy: async () => {
                                    const m = await import('./pages/AdminDeleteOrphans.tsx');
                                    return { Component: m.default };
                                },
                            },
                        ],
                    },
                ],
            },
            // Redirect /zytag to /demo, to not break existing links.
            {
                path: '/zytag',
                loader: () => redirect('/demo'),
                children: [
                    {
                        path: ':tagId',
                        loader: () => redirect(`/demo/`),
                    },
                ],
            },
            {
                path: '*',
                element: <NotFound />,
            },
        ],
    },
]);
Sentry.init({
    dsn: 'https://031405d6e695ae8bae407344ecfbe43f@o4507698333876224.ingest.us.sentry.io/4511344287219712',
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Capture 100% of errors
    sampleRate: 1.0,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1.0,
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    denyUrls: ['app.hopscotch.club'],
});

Sentry.setUser({ email: authService.getUserPrimaryEmail() ?? undefined, id: String(authService.getUserId()) });

initializeHopscotch();

const root = createRoot(document.getElementById('root')!, {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
    <Theme>
        <StrictMode>
            <ZybookAuthProvider>
                <RouterProvider router={router} />
                <SonnerToaster />
            </ZybookAuthProvider>
        </StrictMode>
    </Theme>,
);
